import { useEffect, useState } from 'react';
import { useT } from '@/i18n/useT';
import { errorMessage } from '@/lib/errorMessage';
import { ApiError } from '@/lib/api';
import {
  useBotState,
  useBotQueue,
  usePlaceSignal,
  useQueueBatch,
  useClearQueue,
  useCancelQueued,
} from '@/bot/hooks';
import { BotGate } from './BotGate';
import { BotNav } from './BotNav';
import { ConfirmButton } from './ConfirmButton';
import type { QueueItem } from '@/lib/api';
import {
  convertLocalSignalTimesToUtc,
  describeLocalTimezone,
  localFromUtcHHMM,
  localHHMM,
} from '@/lib/signalTime';

const money = (n: number): string =>
  `${n < 0 ? '-' : n > 0 ? '+' : ''}$${Math.abs(n).toFixed(2)}`;

/**
 * The customer's local wall-clock for a queue row. scheduledFor is null when
 * the signal was already stale at intake, so those fall back to the UTC HH:MM
 * the bot holds, converted to local. Never `new Date(null)`.
 */
const rowClock = (it: QueueItem): string =>
  it.scheduledFor != null
    ? localHHMM(it.scheduledFor)
    : localFromUtcHHMM(it.signal.entryHour, it.signal.entryMinute);

/** Cycle-level outcome first (matches the hero W·L), else trade #0's. */
const rowOutcome = (it: QueueItem): string | undefined =>
  it.cycleOutcome ? (it.cycleOutcome === 'LOSS_MAX' ? 'LOSS' : it.cycleOutcome) : it.outcome;

const rowPnl = (it: QueueItem): number | null =>
  typeof it.cycleProfitUsd === 'number'
    ? it.cycleProfitUsd
    : typeof it.profitUsd === 'number'
      ? it.profitUsd
      : null;

/** "in 4m 12s" for a pending row, as the approved queue ticked every second. */
const countdown = (scheduledFor: number, now: number): string => {
  const s = Math.max(0, Math.round((scheduledFor - now) / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
};

/** A 1 Hz clock, only ticking while there is something to count down. */
function useTicker(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

/** Outcome word at the front of the engine's manual-trade detail line. */
const detailOutcome = (detail: string): 'win' | 'loss' | 'tie' | 'unknown' | null => {
  const m = /^(WIN|LOSS|TIE|UNKNOWN)\b/i.exec(detail.trim());
  return m ? (m[1]!.toLowerCase() as 'win' | 'loss' | 'tie' | 'unknown') : null;
};

/**
 * Trading: place one trade now, or queue a list to fire on a schedule.
 *
 * The single most important detail here is that placing a signal BLOCKS until
 * the trade has settled — roughly 70 seconds for a 60s expiry. The UI has to say
 * so, or a customer assumes it hung and fires a second trade.
 */
export function TradingPage() {
  const { t } = useT();
  const state = useBotState();
  const queue = useBotQueue();
  const place = usePlaceSignal();
  const batch = useQueueBatch();
  const clearQueue = useClearQueue();
  const cancelOne = useCancelQueued();

  const [asset, setAsset] = useState('EURUSD_otc');
  const [direction, setDirection] = useState<'BUY' | 'SELL'>('BUY');
  const [expiry, setExpiry] = useState(60);
  const [amount, setAmount] = useState('');
  const [useMg, setUseMg] = useState(true);
  const [lines, setLines] = useState('');
  const [msg, setMsg] = useState('');
  const [msgTone, setMsgTone] = useState<'ok' | 'warn' | 'loss'>('ok');
  const [err, setErr] = useState('');
  const [lineErrors, setLineErrors] = useState<Array<{ lineNumber: number; rawLine: string; reason: string }>>([]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    try {
      const r = await place.mutateAsync({
        asset: asset.trim(),
        direction,
        expirySeconds: expiry,
        amount: amount.trim() ? Number(amount) : undefined,
        martingaleEnabled: useMg,
      });
      // The approved dashboard coloured the result by outcome; a LOSS or a
      // skip in a green success box is misleading. Skips are the engine's
      // reason, verbatim.
      const o = r.skipped ? null : detailOutcome(r.detail);
      setMsgTone(r.skipped ? 'warn' : o === 'loss' ? 'loss' : o === 'unknown' ? 'warn' : 'ok');
      setMsg(r.detail);
    } catch (e2) {
      setErr(errorMessage(e2));
    }
  };

  const addBatch = async () => {
    setErr('');
    setMsg('');
    setLineErrors([]);
    try {
      // Local clock → UTC, exactly as the approved dashboard did before posting.
      // The bot reads HH:MM as UTC; the customer types their own clock.
      const r = await batch.mutateAsync(convertLocalSignalTimesToUtc(lines));
      // Honest counts. `queued` includes rows the scheduler skipped at intake
      // (scheduledFor null); the approved dashboard subtracted those, and
      // invalid lines are errors, not "done".
      const live = r.queued.filter((q) => q.scheduledFor != null).length;
      const stale = r.queued.length - live;
      setMsgTone(live > 0 ? 'ok' : 'warn');
      setMsg(t('trade.batchResult', { live, stale, invalid: r.errorCount }));
      setLineErrors(r.errors.slice(0, 8));
      // Only clear the box when something is actually going to fire; if every
      // line was past or invalid the customer needs the text to fix it.
      if (live > 0) setLines('');
    } catch (e2) {
      setErr(errorMessage(e2));
      // The gateway refuses a list that uses a timeframe Pocket Option cannot
      // place (M15) BEFORE it reaches the bot, and says which lines. Show them
      // the same way parser errors are shown, so the customer can fix the list.
      const structured = e2 instanceof ApiError ? (e2.body as { errors?: unknown } | undefined)?.errors : undefined;
      if (Array.isArray(structured)) {
        setLineErrors(
          (structured as Array<{ lineNumber: number; rawLine: string; reason: string }>).slice(0, 8)
        );
      }
    }
  };

  const pendingCount = queue.data?.summary.pending ?? 0;
  const now = useTicker(pendingCount > 0);

  return (
    <BotGate query={state}>
      {(s) => {
        const q = queue.data;
        const pending = q?.summary.pending ?? 0;
        const done = (q?.summary.completed ?? 0) + (q?.summary.skipped ?? 0);
        const canTrade = s.control.running && !s.halted;

        return (
          <>
            {err ? <div className="bot-alert error">{err}</div> : null}
            {msg ? <div className={`bot-alert ${msgTone}`}>{msg}</div> : null}
            {lineErrors.length > 0 ? (
              <ul className="bot-line-errors">
                {lineErrors.map((e) => (
                  <li key={e.lineNumber}>
                    <span className="muted">{t('trade.lineN', { n: e.lineNumber })}</span> <code>{e.rawLine}</code> · {e.reason}
                  </li>
                ))}
              </ul>
            ) : null}

            <section className="bot-card">
              <div className="bot-card-head"><h2>{t('trade.placeSignal')}</h2></div>
              <form onSubmit={submit} className="bot-form">
                <label className="bot-field">
                  <span>{t('trade.asset')}</span>
                  <input value={asset} onChange={(e) => setAsset(e.target.value)} disabled={place.isPending} />
                </label>
                <div className="bot-field">
                  <span>{t('trade.direction')}</span>
                  <div className="bot-seg">
                    <button type="button" className={direction === 'BUY' ? 'on up' : ''} onClick={() => setDirection('BUY')} disabled={place.isPending}>
                      {t('trade.buy')}
                    </button>
                    <button type="button" className={direction === 'SELL' ? 'on down' : ''} onClick={() => setDirection('SELL')} disabled={place.isPending}>
                      {t('trade.sell')}
                    </button>
                  </div>
                </div>
                <div className="bot-grid2">
                  <label className="bot-field">
                    <span>{t('trade.expiry')}</span>
                    <input type="number" min={5} value={expiry} onChange={(e) => setExpiry(Number(e.target.value))} disabled={place.isPending} />
                  </label>
                  <label className="bot-field">
                    <span>{t('trade.amount')}</span>
                    <input type="number" step="0.01" min="0" placeholder={String(s.nextStake)} value={amount} onChange={(e) => setAmount(e.target.value)} disabled={place.isPending} />
                  </label>
                </div>
                <label className="bot-check">
                  <input type="checkbox" checked={useMg} onChange={(e) => setUseMg(e.target.checked)} disabled={place.isPending} />
                  <span>{t('trade.useMartingale')}</span>
                </label>
                <button type="submit" className="bot-btn primary" disabled={place.isPending || !canTrade}>
                  {place.isPending ? t('trade.placing') : t('trade.place')}
                </button>
                {!canTrade ? <p className="muted">{s.halted ? s.haltReason : t('dash.pausedBlurb')}</p> : null}
              </form>
            </section>

            <section className="bot-card">
              <div className="bot-card-head"><h2>{t('trade.uploadTitle')}</h2></div>
              <p className="muted">{t('trade.uploadHint')}</p>
              {/* The approved hint told the customer their times are read in
                  their own clock and named the timezone. Without it, a
                  Haitian customer has no way to know 10:30 is not 10:30 UTC. */}
              <p className="muted bot-tz-hint">{t('trade.tzHint', { tz: describeLocalTimezone() })}</p>
              <textarea
                className="bot-textarea"
                rows={5}
                value={lines}
                onChange={(e) => setLines(e.target.value)}
                disabled={batch.isPending}
                spellCheck={false}
              />
              <button type="button" className="bot-btn primary" onClick={addBatch} disabled={batch.isPending || !lines.trim()}>
                {batch.isPending ? t('common.saving') : t('trade.upload')}
              </button>
            </section>

            <section className="bot-card">
              <div className="bot-card-head">
                <h2>{t('trade.queueTitle')}</h2>
                <span className="muted">{t('trade.queueCounts', { pending, done })}</span>
              </div>
              {!q || q.items.length === 0 ? (
                <p className="muted">{t('trade.queueEmpty')}</p>
              ) : (
                <ul className="bot-list">
                  {q.items.slice(0, 40).map((it) => {
                    const outcome = rowOutcome(it);
                    const pnl = rowPnl(it);
                    const outcomeClass =
                      outcome === 'WIN' ? 'win' : outcome === 'LOSS' || outcome === 'ABANDONED' ? 'loss' : '';
                    return (
                      <li key={it.id} className={`bot-list-row st-${it.status} ${outcomeClass}`}>
                        <div className="bot-list-main">
                          <div>
                            <span className={it.signal.direction === 'BUY' ? 'up' : 'down'}>
                              {it.signal.direction === 'BUY' ? '▲' : '▼'} {it.signal.direction}
                            </span>{' '}
                            <strong>{it.signal.pair ?? it.signal.asset}</strong>
                            {it.signal.market === 'otc' ? <span className="muted"> OTC</span> : null}
                            <span className="muted"> {it.signal.timeframe}</span>
                          </div>
                          {/* Second line: the customer's LOCAL time, then what
                              happened. Skip reasons are shown, as the approved
                              queue did — a bare SKIPPED badge is what made the
                              customer think the parser had failed. */}
                          <div className="muted bot-list-sub">
                            {t('trade.rowEntry', { time: rowClock(it) })}
                            {it.status === 'pending' && it.scheduledFor != null
                              ? (it.scheduledFor - now > 1500
                                  ? ` · ${t('trade.inTime', { t: countdown(it.scheduledFor, now) })}`
                                  : ` · ${t('trade.firingNow')}`)
                              : null}
                            {it.status === 'executing' ? ` · ${t('trade.executingNow')}` : null}
                            {it.status === 'completed' && outcome
                              ? ` · ${t(`status.${outcome.toLowerCase()}`)}${pnl !== null ? ` · ${money(pnl)}` : ''}`
                              : null}
                            {(it.status === 'skipped' || it.status === 'cancelled' || it.status === 'unknown') && it.detail
                              ? ` · ${/has already passed/i.test(it.detail) ? t('trade.skipPast') : it.detail.slice(0, 90)}`
                              : null}
                          </div>
                        </div>
                        <div className="bot-list-right">
                          {it.status === 'completed' && pnl !== null ? (
                            <span className={pnl >= 0 ? 'up' : 'down'}>{money(pnl)}</span>
                          ) : null}
                          <span className={`bot-pill ${it.status} ${outcomeClass}`}>{t(`status.${it.status}`)}</span>
                          {it.status === 'pending' ? (
                            <button type="button" className="bot-btn tiny ghost" onClick={() => cancelOne.mutate(it.id)} disabled={cancelOne.isPending}>
                              {t('trade.cancelOne')}
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {q && q.items.length > 0 ? (
                <ConfirmButton
                  label={t('trade.clearQueue')}
                  confirmText={t('trade.confirmClearQueue')}
                  busy={clearQueue.isPending}
                  onConfirm={() => clearQueue.mutate()}
                />
              ) : null}
            </section>

            <BotNav />
          </>
        );
      }}
    </BotGate>
  );
}
